import { TZDate } from '@date-fns/tz';
import clsx from 'clsx';
import { endOfDay, isSameDay } from 'date-fns';
import { useContext, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { formatDate } from '@dynatrace-sdk/units';

import {
  isFullTimeframe,
  isPartialTimeframe,
  isNowTimeValue,
} from '../../../core/time/helpers.js';
import { useCalendarStore } from '../../../core/time/timeframeStore/useTimeframeStore.js';
import { isOutOfBounds } from '../../../core/time/validation.js';
import { useFormFieldContext } from '../../forms-core/hooks/useFormFieldContext.js';
import { useFormFieldMessagesContext } from '../../forms-core/hooks/useFormFieldMessagesContext.js';
import * as styles from '../Calendar.sty.js';
import { CalendarItem } from './CalendarItem.js';
import { CalendarOutlineIcon } from './CalendarOutlineIcon.js';
import { DayViewContext } from '../contexts/DayViewContext.js';
import { useCalendarContext } from '../hooks/useCalendarContext.js';
import { useFocus } from '../hooks/useFocus.js';
import { NUMBER_OF_WEEKDAYS } from '../types/calendarConfiguration.js';
import { getHighlightingProps } from '../utils/helpers.js';

/** @internal */
interface DayProps {
  /** The day it represents. */
  date: TZDate;
  /** Whether the day is part of the current month */
  isCurrentMonthDay?: boolean;
  /** Position in the calendar grid 1 is first day and 7 the last */
  position: number;
}

/**
 * Day component
 * Renders one single day of a calendar month
 * @internal
 */
export const Day = ({ date, isCurrentMonthDay = true, position }: DayProps) => {
  // Reference of the day button, needed to pass to the hook for proper focus management
  const itemRef = useRef<HTMLDivElement>(null);
  const isLastInRow = position % NUMBER_OF_WEEKDAYS === 0;
  const isFirstInRow = position % NUMBER_OF_WEEKDAYS === 1;
  const intl = useIntl();

  const min = useCalendarStore((store) => store.min);
  const max = useCalendarStore((store) => store.max);
  const value = useCalendarStore((store) => store.calendarValue ?? null);
  const setCalendarValue = useCalendarStore((store) => store.setCalendarValue);

  const { state, dispatch } = useCalendarContext();
  const { hoveredDate, setHoveredDate } = useContext(DayViewContext);

  const ariaLabel = useMemo(() => {
    const formatMessage = intl.formatMessage({
      defaultMessage: 'Select day',
      id: 'UBJSASimMVkN5w7p',
      description: 'Aria label to add to each day button',
    });
    return `${formatMessage} ${formatDate(date, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    })}`;
  }, [date, intl]);

  const isCurrent = isSameDay(date, state.today);
  const isFocused = isSameDay(date, state.activeDate);

  const isDisabled = useMemo(() =>
    // A day is disabled if its end is before the min, or if its start is after the max.
    {
      return !!(
        isOutOfBounds(endOfDay(date), min) ||
        isOutOfBounds(date, undefined, max)
      );
    }, [date, min, max]);

  const { tabIndex } = useFocus({ isFocused, itemRef });

  /** Handle click on a day in the select day view. */
  function onClick() {
    dispatch({ type: 'shouldUpdateFocusFromKeydown', payload: false });
    setCalendarValue?.(date);
  }

  const {
    highlightType,
    highlightColor,
    isSelected,
    selectionVariant,
    isIncompleteSelection,
  } = useMemo(
    () => getHighlightingProps(date, value, state.type, hoveredDate),
    [date, value, state.type, hoveredDate],
  );

  const rangeInteractionProps = useMemo(() => {
    const onMouseLeave =
      (isFullTimeframe(value) || value === null) && hoveredDate
        ? () => setHoveredDate?.(null)
        : undefined;

    // Suppress the hover preview when the partial selection has a "now"
    // boundary that falls outside [min, max] — the preview would anchor to an
    // invalid time and mislead the user.
    const hasOutOfBoundsNow =
      isPartialTimeframe(value) &&
      (isNowTimeValue(value?.from) || isNowTimeValue(value?.to)) &&
      isOutOfBounds('now', min, max) !== undefined;

    const onMouseEnter =
      isPartialTimeframe(value) && !hasOutOfBoundsNow
        ? () => setHoveredDate?.(date)
        : undefined;

    return {
      onMouseLeave,
      onMouseEnter,
    };
  }, [value, hoveredDate, date, setHoveredDate, min, max]);

  const { disabled, ariaDisabled } = useFormFieldContext();
  const {
    state: { showError },
  } = useFormFieldMessagesContext();

  const validatedHighlightColor =
    showError && highlightColor === 'primary' ? 'critical' : highlightColor;
  const className = clsx(
    styles.field({
      color: validatedHighlightColor,
      isDisabled: isDisabled || disabled || ariaDisabled,
      isIncompleteSelection: isIncompleteSelection,
    }),
    highlightType && highlightType !== 'same'
      ? styles.rangeField({
          rangeKind: highlightType,
        })
      : '',
    isSelected ? styles.clearBackground : '',
  );
  const dayValue = date.getDate();

  return (
    <td role="presentation" className={className} {...rangeInteractionProps}>
      <CalendarItem
        ref={itemRef}
        className="calendar-button calendar-body-button"
        isDisabled={isDisabled || disabled}
        isCurrent={isCurrent}
        isSelected={isSelected}
        highlightColor={validatedHighlightColor}
        isCurrentMonthDay={isCurrentMonthDay}
        tabIndex={tabIndex}
        ariaLabel={ariaLabel}
        role="gridcell"
        aria-selected={isSelected}
        onClick={onClick}
        view="day"
        data-dt-children-name="SelectDay"
        data-dt-properties={`value:${dayValue}`}
      >
        {dayValue}
        <CalendarOutlineIcon
          isCurrent={isCurrent}
          variant={selectionVariant}
          highlightType={
            (isFirstInRow && highlightType === 'last') ||
            (isLastInRow && highlightType === 'first')
              ? undefined
              : highlightType
          }
        />
      </CalendarItem>
    </td>
  );
};

Day['displayName'] = 'Day';
